import type { TaskCategory, TaskTemplate } from "@/constants/task-templates";

/**
 * Tool representation for integration selection
 */
export interface Tool {
	id: string; // String representation of app_integration_id for UI purposes
	name: string;
	appIntegrationId: number; // Numeric ID from the catalog
	isActive: boolean;
	integrationId?: number; // User's integration ID (if connected)
	provider: string; // Provider name (e.g., "google")
	logoUrl?: string | null; // Logo URL from app integration catalog
	isPopular?: boolean; // Whether the app is marked as popular
}

/**
 * Get next state for selected integrations when toggling an integration
 * Pure function for easier testing and state management
 */
export function getNextToolsState(currentTools: string[], toolId: string): string[] {
	// If selecting "auto", clear all other selections
	if (toolId === "auto") {
		return ["auto"];
	}

	// If selecting a specific integration, remove "auto"
	const withoutAuto = currentTools.filter((id) => id !== "auto");

	// Toggle the integration
	if (withoutAuto.includes(toolId)) {
		const updated = withoutAuto.filter((id) => id !== toolId);
		// If no integrations selected, default to auto
		return updated.length === 0 ? ["auto"] : updated;
	}

	return [...withoutAuto, toolId];
}

/**
 * Get next state for selected integrations when toggling an integration
 * Pure function for easier testing and state management
 */
export function getNextIntegrationsState(
	currentIntegrations: string[],
	integrationId: string
): string[] {
	if (currentIntegrations.includes(integrationId)) {
		return currentIntegrations.filter((id) => id !== integrationId);
	}
	return [...currentIntegrations, integrationId];
}

/**
 * Get display label for selected integrations
 */
export function getToolsLabel(selectedTools: string[], combinedTools: Tool[]): string {
	if (selectedTools.includes("auto") || selectedTools.length === 0) {
		return "Auto";
	}
	if (selectedTools.length === 1) {
		const tool = combinedTools.find((t) => t.id === selectedTools[0]);
		return tool?.name || "Auto";
	}
	return `${selectedTools.length} integrations`;
}

/**
 * Filter templates based on selected category and integrations
 */
export function filterTemplates(
	templates: TaskTemplate[],
	selectedCategory: TaskCategory,
	selectedIntegrations: string[]
): TaskTemplate[] {
	return templates.filter((template) => {
		// Filter by category
		if (selectedCategory !== "All" && template.category !== selectedCategory) {
			return false;
		}

		// Filter by integrations (if any selected, template must have at least one)
		if (selectedIntegrations.length > 0) {
			const hasMatchingIntegration = template.integrations.some((i) =>
				selectedIntegrations.includes(typeof i === "string" ? i : i.id)
			);
			if (!hasMatchingIntegration) {
				return false;
			}
		}

		return true;
	});
}

/**
 * Paginate templates array
 */
export function paginateTemplates(
	templates: TaskTemplate[],
	currentPage: number,
	pageSize: number
): TaskTemplate[] {
	const startIndex = (currentPage - 1) * pageSize;
	const endIndex = startIndex + pageSize;
	return templates.slice(startIndex, endIndex);
}

/**
 * Calculate total number of pages for pagination
 */
export function getTotalPages(totalItems: number, pageSize: number): number {
	return Math.ceil(totalItems / pageSize);
}
